/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { t } from '@sportninja/common/i18n'
import { setTimeToBeDefined } from '@sportninja/common/ts-utils/date-utils'

import { Flex } from '../Layout'
import DatePicker from '../DatePicker'
import { inputCss, Label, InputWrapper } from '../Form/css'
import { toISOString } from '../../utils/utils'

export const getDurationInMinutes = (start, end) => {
  const diff = dayjs(end).diff(dayjs(start), 'minute')
  return diff
}

const DateAndDurationPicker = ({
  errors = {},
  datePlaceholder,
  disabled,
  defaultValues = {},
  onChange,
  ...props
}) => {
  const firstUpdate = useRef(true)
  const firstUpdate2 = useRef(true)
  const { starts_at = {}, ends_at = {} } = defaultValues
  // `props.values` is the form's live state; the bare prop is only the initial
  // value. Check for presence, not truthiness -- a truthiness test discards
  // the live `false` written when the user turns the toggle OFF, leaving the
  // component on the stale `true` so the sentinel is never cleared.
  const { time_to_be_defined_status } =
    props?.values && 'time_to_be_defined_status' in props.values
      ? props.values
      : props

  const initialStart = starts_at.value ? new Date(starts_at.value) : undefined

  const [start, setStart] = useState(initialStart)
  const [duration, setDuration] = useState(
    ends_at.value ? getDurationInMinutes(initialStart, ends_at.value) : ''
  )

  const handleChange = (method, { target }) => method(target.value)

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false
      return
    }

    // `start` always holds the real picked time; the 23:58:58 UTC sentinel
    // that isTimeToBeDefined() detects is applied only here, on the way out.
    // ends_at is derived from the same value so both stay consistent with the
    // create-game path.
    const isTBD = !!(time_to_be_defined_status && start)
    const startValue = isTBD
      ? setTimeToBeDefined(dayjs(start).toISOString())
      : toISOString(dayjs(start))

    let end
    if (isTBD) {
      end = dayjs(startValue).add(duration, 'minute')
    } else {
      // Keep existing behavior when time_to_be_defined_status is false
      end = dayjs(start).add(duration, 'minute')
    }
    if (start) {
      onChange({
        target: {
          name: 'starts_at',
          value: startValue,
        },
      })
    }
    if (end) {
      onChange({
        target: {
          name: 'ends_at',
          value: toISOString(end),
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, duration, time_to_be_defined_status])

  const sharedDurationProps = {
    disabled,
    required: ends_at.required,
    hasError: !!errors.ends_at,
  }

  const handleTimeTDBChange = useCallback(
    (checked) => {
      // When switching ON (true), stamp the TBD sentinel onto starts_at so the
      // saved game is detectable as TBD even if nothing else changes.
      if (checked && start) {
        onChange({
          target: {
            name: 'starts_at',
            value: setTimeToBeDefined(dayjs(start).toISOString()),
          },
        })
      }

      // When switching OFF (false), pre-select the current time of day while
      // keeping the chosen date -- a game saved as TBD has no real time to
      // restore, and this also moves the value off the 23:58:58 sentinel so
      // it stops reading as TBD.
      //
      // NOTE: setStart must receive a STRING. DatePicker only re-syncs its
      // displayed day when `defaultValue` is a string (see its effect on
      // `defaultValue`), so passing a Date here would leave the picker
      // showing the old time while we save the new one.
      if (!checked) {
        const now = dayjs()
        const updatedStart = start
          ? dayjs(start)
              .hour(now.hour())
              .minute(now.minute())
              .second(now.second())
              .millisecond(now.millisecond())
          : now

        setStart(toISOString(updatedStart))
        onChange({
          target: {
            name: 'starts_at',
            value: toISOString(updatedStart),
          },
        })
      }
      onChange({
        target: {
          name: 'time_to_be_defined_status',
          value: checked,
        },
      })
    },
    [onChange, start]
  )

  const handleDurationChange = useCallback((value) => {
    setDuration(value)
    onChange({
      target: {
        name: 'duration',
        value: value,
      },
    })
  }, [])

  useEffect(() => {
    if (firstUpdate2.current) {
      firstUpdate2.current = false
      return
    }
    handleTimeTDBChange(time_to_be_defined_status)
  }, [time_to_be_defined_status])

  return (
    <Fragment>
      <DatePicker
        datePlaceholder={datePlaceholder}
        defaultValue={start}
        disabled={disabled}
        hasError={!!errors.starts_at}
        name='starts_at'
        label={starts_at.label}
        onChange={handleChange.bind(this, setStart)}
        required={starts_at.required}
        timezone={starts_at.timezone}
        time_to_be_defined_status={time_to_be_defined_status}
        showTimeTDBToggle={props.showTimeTDBToggle}
        onTimeTDBChange={handleTimeTDBChange}
      />
      <InputWrapper {...sharedDurationProps}>
        <Label {...sharedDurationProps}>
          {ends_at.label}
          {ends_at.required ? ' *' : ''}
        </Label>
        <Flex>
          <input
            css={[
              inputCss(sharedDurationProps),
              css`
                padding-right: 88px;
              `,
            ]}
            disabled={disabled}
            min='0'
            // onChange={handleChange.bind(this, setDuration)}
            onChange={(e) => handleDurationChange(e.target.value)}
            required={ends_at.required}
            step='1'
            style={{ width: 'calc(100% + 100px)' }}
            type='number'
            value={duration}
          />
          <div
            css={css`
              position: absolute;
              top: calc(50% + 3px);
              right: 16px;
            `}
          >
            {t('common:minutes')}
          </div>
        </Flex>
      </InputWrapper>
    </Fragment>
  )
}

DateAndDurationPicker.propTypes = {
  errors: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  datePlaceholder: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  defaultValues: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  showTimeTDBToggle: PropTypes.bool,
  onTimeTDBChange: PropTypes.func,
}

export default DateAndDurationPicker
